import { useState, useMemo, useEffect, useCallback } from 'react';

import { ResourceType } from 'services/resources';
import ContainerClassesService from 'services/containerClasses';
import useResource from 'hooks/useResource';
import useQueryParam from './useQueryParam';

import {
  mapAgenciesToOptions,
  mapDoorsToOptions,
  mapEmployeesToOptions,
  mapLocationsToOptions,
  mapContainerClassesToOptions,
  mapCustomersToOptions,
} from 'helpers/mapToOptions';

const useSelect = (opts = {}) => {
  const {
    getOptions,
    hasAllOption,
    selectFirst = true,
    allOptionLabel = 'All',
    allOptionKey = 'all',
    initialValue = null,
    queryParam = '',
  } = opts;

  const [queryValue, setQueryValue] = useQueryParam(queryParam, initialValue);
  const [value, setValue] = useState(() => {
    if (queryParam) {
      if (queryValue && /^[0-9]+$/g.test(queryValue)) {
        return parseInt(queryValue, 10);
      } else {
        return queryValue;
      }
    } else {
      return initialValue;
    }
  });
  const selectOptions = useMemo(() => {
    const options = getOptions();
    if (hasAllOption) {
      options.unshift({
        label: allOptionLabel,
        value: allOptionKey,
      });
    }
    if (selectFirst && options.length && value === null) {
      setValue(options[0].value);
    }
    return options;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getOptions]);

  useEffect(() => {
    if (queryParam) {
      setQueryValue(value);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParam, value]);

  const onChange = useCallback((value) => {
    setValue(value);
  }, []);

  return {
    options: selectOptions,
    value,
    onChange,
  };
};

export const useLocationSelect = (opts = {}) => {
  const locations = useResource(ResourceType.locations, { prefetch: true });

  const getOptions = useCallback(() => {
    return mapLocationsToOptions(locations.state.list);
  }, [locations.state.list]);

  return useSelect({
    ...opts,
    getOptions,
    allOptionLabel: 'All Locations',
  });
};

export const useDoorSelect = (opts = {}) => {
  const { locationId, ...restOps } = opts;
  const doors = useResource(ResourceType.doors, { prefetch: true });

  const getOptions = useCallback(() => {
    return mapDoorsToOptions(doors.state.list.filter(door => {
      return door.location_id === locationId;
    }));
  }, [doors.state.list, locationId]);

  return useSelect({
    ...restOps,
    getOptions,
    allOptionLabel: 'All Doors',
  });
};

export const useEmployeeSelect = (opts = {}) => {
  const employees = useResource(ResourceType.employees, { prefetch: true });

  const getOptions = useCallback(() => {
    return mapEmployeesToOptions(employees.state.list);
  }, [employees.state.list]);

  return useSelect({
    ...opts,
    getOptions,
    allOptionLabel: 'All Individuals',
  })
};

export const useAgencySelect = (opts = {}) => {
  const agencies = useResource(ResourceType.agencies, { prefetch: true });

  const getOptions = useCallback(() => {
    return mapAgenciesToOptions(agencies.state.list);
  }, [agencies.state.list]);

  return useSelect({
    ...opts,
    getOptions,
    allOptionLabel: 'All Agencies',
  });
};

export const useContainerClassSelect = (opts = {}) => {
  const [containerClasses, setContainerClasses] = useState([]);

  useEffect(() => {
    const service = new ContainerClassesService();
    service.loadAll().then((data) => {
      if (!data) return;
      setContainerClasses(data);
    });
  }, []);

  const getOptions = useCallback(() => {
    return mapContainerClassesToOptions(containerClasses);
  }, [containerClasses]);
  
  return useSelect({
    ...opts,
    getOptions,
    allOptionLabel: 'All Container Types',
  });
};

export const useCustomerSelect = (opts = {}) => {
  const customers = useResource(ResourceType.customers, { prefetch: true });
  
  const getOptions = useCallback(() => {
    return mapCustomersToOptions(customers.state.list);
  }, [customers.state.list]);

  return useSelect({
    ...opts,
    getOptions,
    allOptionLabel: 'All Customers',
  });
};

export default useSelect;
